"use client";

import { useState } from "react";
import { ShoppingCart, CheckCircle2 } from "lucide-react";
import axios from "axios";

const PUBLIC_API =
  (process.env.NEXT_PUBLIC_LANDING_API_URL?.replace(/\/$/, "") || "http://localhost:3820/api/v1") + "/public";

const PRODUCTS = [
  "Nước đóng chai 350ml (thùng 24 chai)",
  "Nước đóng chai 500ml (thùng 24 chai)",
  "Nước đóng bình 15L",
  "Nước đóng bình 20L",
  "Đá dùng liền 5kg",
  "Đặt hỗn hợp nhiều loại",
];

type Form = {
  name: string;
  phone: string;
  address: string;
  product: string;
  quantity: string;
  note: string;
};

const EMPTY: Form = {
  name: "",
  phone: "",
  address: "",
  product: "",
  quantity: "",
  note: "",
};

const inputCls =
  "w-full rounded-lg bg-blue-900/50 border border-blue-600/50 px-4 py-2.5 text-white placeholder-blue-400 focus:outline-none focus:border-blue-300 transition text-sm";

const labelCls = "block text-sm font-medium text-blue-200 mb-1";

export default function OrderForm() {
  const [form, setForm] = useState<Form>(EMPTY);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  function set(field: keyof Form) {
    return (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >
    ) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${PUBLIC_API}/orders`, {
        name: form.name,
        phone: form.phone,
        address: form.address,
        product: form.product,
        quantity: Number(form.quantity),
        note: form.note || undefined,
      });
      setSubmitted(true);
    } catch {
      alert("Gửi đơn thất bại. Vui lòng thử lại hoặc gọi trực tiếp 091 6253 399.");
    } finally {
      setLoading(false);
    }
  }

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-10 text-center">
        <CheckCircle2 size={56} className="text-emerald-400" />
        <h3 className="text-2xl font-bold text-white">Đặt hàng thành công!</h3>
        <p className="text-blue-200 max-w-sm text-sm leading-relaxed">
          Chúng tôi đã nhận được đơn hàng của bạn.
          <br />
          Nhân viên sẽ gọi xác nhận trong vòng <strong className="text-white">30 phút</strong>.
        </p>
        <button
          onClick={() => { setForm(EMPTY); setSubmitted(false); }}
          className="mt-2 text-sm text-blue-300 underline hover:text-white transition-colors"
        >
          Đặt thêm đơn hàng mới
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Họ tên + SĐT */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>
            Họ và tên <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            required
            value={form.name}
            onChange={set("name")}
            placeholder="Nguyễn Văn A"
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>
            Số điện thoại <span className="text-red-400">*</span>
          </label>
          <input
            type="tel"
            required
            value={form.phone}
            onChange={set("phone")}
            placeholder="09xx xxx xxx"
            className={inputCls}
          />
        </div>
      </div>

      {/* Địa chỉ giao hàng */}
      <div>
        <label className={labelCls}>
          Địa chỉ giao hàng <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          required
          value={form.address}
          onChange={set("address")}
          placeholder="Số nhà, tên đường, phường/xã, quận/huyện, tỉnh/thành"
          className={inputCls}
        />
      </div>

      {/* Sản phẩm + Số lượng */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>
            Sản phẩm <span className="text-red-400">*</span>
          </label>
          <select
            required
            value={form.product}
            onChange={set("product")}
            className={inputCls + " appearance-none cursor-pointer"}
          >
            <option value="" disabled>-- Chọn sản phẩm --</option>
            {PRODUCTS.map((p) => (
              <option key={p} value={p} className="bg-blue-900 text-white">
                {p}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>
            Số lượng <span className="text-red-400">*</span>
          </label>
          <input
            type="number"
            required
            min={1}
            value={form.quantity}
            onChange={set("quantity")}
            placeholder="VD: 2 thùng, 5 bình..."
            className={inputCls}
          />
        </div>
      </div>

      {/* Ghi chú */}
      <div>
        <label className={labelCls}>Ghi chú thêm</label>
        <textarea
          rows={3}
          value={form.note}
          onChange={set("note")}
          placeholder="Thời gian giao hàng, yêu cầu đặc biệt, hỏi giá sỉ..."
          className={inputCls + " resize-none"}
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        className="inline-flex w-full justify-center items-center gap-2 rounded-lg bg-white px-6 py-3 font-bold text-[color:var(--brand-dark)] hover:bg-blue-50 disabled:opacity-60 transition shadow-lg text-sm"
      >
        {loading ? (
          <span className="inline-block h-4 w-4 border-2 border-blue-700 border-t-transparent rounded-full animate-spin" />
        ) : (
          <ShoppingCart size={16} />
        )}
        {loading ? "Đang gửi đơn hàng..." : "Gửi đơn hàng"}
      </button>

      <p className="text-center text-xs text-blue-400">
        Hoặc gọi trực tiếp:{" "}
        <a href="tel:0916253399" className="text-white font-semibold hover:underline">
          091 6253 399
        </a>{" "}
        ·{" "}
        <a href="tel:0982383288" className="text-white font-semibold hover:underline">
          098 2383 288
        </a>
      </p>
    </form>
  );
}
